import { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button } from '@shadcn/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shadcn/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/components/ui/table';
import { Input } from '@shadcn/components/ui/input';
import { Switch } from '@shadcn/components/ui/switch';

import {  ArrowLeftIcon, Edit, Save, X } from 'lucide-react';
import { AddOrderSourceDialog } from './components/AddOrderSourceDialog';
import { fetchRegionConfigDetails, updateRegionConfig } from './RegionConfig.service';

function RegionConfigDetail() {
  const { regionId } = useParams();
  const navigate =  useHistory();
  const [config, setConfig] = useState(null);
  const [orderSources, setOrderSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddSourceDialogOpen, setIsAddSourceDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    locale: '',
    orderSources: [],
    isActive: true
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function loadRegionConfigDetails() {
      try {
        setIsLoading(true);
        const {region, orderSources} = await fetchRegionConfigDetails(regionId);
        setConfig(region);
        setOrderSources(orderSources);
        // Initialize edit form with current values
        setEditForm({
          locale: region.locale || '',
          orderSources: region.orderSources || [],
          isActive: region.isActive
        });
      } catch (error) {
        console.error('Failed to load region config details:', error);
      } finally {
        setIsLoading(false);
      }
    }
    
    loadRegionConfigDetails();
  }, [regionId]);

  const handleBackToList = () => {
    navigate.push('/region-configs/list');
  };

  const handleAddOrderSource = (newSource) => {
    setConfig(newSource);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditForm({
      locale: config.locale || '',
      orderSources: config.orderSources || [],
      isActive: config.isActive
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      locale: config.locale || '',
      orderSources: config.orderSources || [],
      isActive: config.isActive
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updatedConfig = await updateRegionConfig(regionId, editForm);
      setConfig(prev => ({ ...prev, ...editForm }));
      setIsEditing(false);
      console.log('Region config updated successfully');
    } catch (error) {
      console.error('Failed to update region config:', error);
      alert('Failed to update region config');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOrderSourcesChange = (value) => {
    
    if (!value || value.trim() === '') {
      setEditForm(prev => ({ ...prev, orderSources: [] }));
      return;
    }
    
    
    const sources = value
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .filter((source, index, arr) => arr.indexOf(source) === index); // Remove duplicates
    
    
    if (value.endsWith(',')) {
      value = value.slice(0, -1);
      sources.push('');
    }
    
    setEditForm(prev => ({ ...prev, orderSources: sources }));
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-full">Loading...</div>;
  }

  if (!config) {
    return <div className="text-center p-4">Region Config not found</div>;
  }

  return (
    <div className="page-content">
      <div className="w-full px-3 mx-auto xl:max-w-[85%] mb-6">
      <Button 
        variant="outline" 
        className="mb-4"
        onClick={handleBackToList}
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2" /> Back
      </Button>
      
      
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl font-bold">
            Region Config Details: {config.regionId}
          </CardTitle>
          <div className="flex space-x-2">
            {!isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleEdit}
                >
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
                {config.type === 'online' && (
                  <Button 
                    size="sm" 
                    onClick={() => setIsAddSourceDialogOpen(true)}
                  >
                    Add Order Source
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 mr-2" /> Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <Save className="h-4 w-4 mr-2" /> {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-500 min-w-[100px]">Name:</span>
              <span>{config.name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-500 min-w-[100px]">Region ID:</span>
              <span>{config.regionId}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-500 min-w-[100px]">Type:</span>
              <span className="capitalize">{config.type}</span>
            </div>
            
            {/* Editable Status Field */}
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-500 min-w-[100px]">Status:</span>
              {isEditing ? (
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={editForm.isActive}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, isActive: checked }))}
                  />
                  <span className="text-sm">{editForm.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              ) : (
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${config.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {config.isActive ? 'Active' : 'Inactive'}
                </span>
              )}
            </div>

            {/* Editable Locale Field */}
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-500 min-w-[100px]">Locale:</span>
              {isEditing ? (
                <Input
                  value={editForm.locale}
                  onChange={(e) => setEditForm(prev => ({ ...prev, locale: e.target.value }))}
                  placeholder="Enter locale (e.g., AU, US, UK)"
                  className="w-48"
                />
              ) : (
                <span>{config.locale || 'Not set'}</span>
              )}
            </div>
            
            {config.type === 'online' && (
              <div className="col-span-2 flex items-start space-x-2 mt-2">
                <span className="font-medium text-gray-500 min-w-[100px]">Order Sources:</span>
                <div className="flex-1">
                  {isEditing ? (
                    <div className="space-y-2">
                      <Input
                        value={editForm.orderSources.join(', ')}
                        onChange={(e) => handleOrderSourcesChange(e.target.value)}
                        placeholder="e.g. shopify_au, shopify_nz"
                        className="w-full"
                      />
                    
                      {editForm.orderSources.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-gray-600 mb-1">Preview:</p>
                          <div className="flex flex-wrap gap-1">
                            {editForm.orderSources.map((source, index) => (
                              <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                {source}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {config.orderSources && config.orderSources.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {config.orderSources.map((source, index) => (
                            <div key={index} className="flex items-center">
                              <span className="bg-gray-100 px-3 py-1 rounded-md text-sm">{source}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {config.type === 'retail' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xl">Order Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead width="25%">Order Source</TableHead>
                  <TableHead width="20%">Type</TableHead>
                  <TableHead width="30%">Shopify Store</TableHead>
                  <TableHead width="25%">Pick Up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderSources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell className="font-medium">{source.orderSource}</TableCell>
                    <TableCell>{source.type}</TableCell>
                    <TableCell>{source.shopifyStoreName || '-'}</TableCell>
                    <TableCell>{source.pickupType || '-'}</TableCell>
                  </TableRow>
                ))}
                {orderSources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                      No Order Sources
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      

      <AddOrderSourceDialog
        open={isAddSourceDialogOpen}
        onOpenChange={setIsAddSourceDialogOpen}
        regionId={regionId}
        onSuccess={handleAddOrderSource}
      />
      </div>
    </div>
  );
}

export default RegionConfigDetail; 
